/**
 * SeeMe Keyboard Shortcuts
 * Shared shortcut registry with auto-generated cheat sheet.
 *
 * Register shortcuts:
 *   SeeMe.shortcuts.register('/', { handler: fn, label: 'Focus search' })
 *   SeeMe.shortcuts.register('cmd+k', { handler: fn, label: 'Spotlight search' })
 *   SeeMe.shortcuts.register('c', { handler: fn, label: 'Create task', when: fn })
 *
 * Options:
 *   handler      — function(e), required (omit for display-only cheat sheet entries)
 *   label        — string, shown in cheat sheet (omit to hide from cheat sheet)
 *   section      — string, cheat sheet group heading (default: 'Global')
 *   when         — function(), shortcut only fires when this returns true
 *   allowEditing — boolean, fires even in text inputs (default: false)
 *   priority     — number, higher = checked first (default: 0)
 *
 * Key format:
 *   Single keys:   '/', 'c', '?', '1'
 *   With modifier:  'cmd+k', 'cmd+enter', 'cmd+1', 'shift+tab', 'alt+n'
 *   cmd matches both Cmd (Mac) and Ctrl (Win/Linux)
 *
 * Built-in:
 *   ? — Show keyboard shortcuts cheat sheet
 *
 * API:
 *   SeeMe.shortcuts.register(key, opts)
 *   SeeMe.shortcuts.unregister(key, section?)
 *   SeeMe.shortcuts.showCheatSheet()
 *   SeeMe.shortcuts.closeCheatSheet()
 *   SeeMe.shortcuts.isCheatSheetOpen()
 *   SeeMe.shortcuts.isEditing()
 */

var SeeMe = window.SeeMe || {};

SeeMe.shortcuts = (function () {

    var registry = [];
    var cheatEl = null;
    var cheatBackdrop = null;
    var savedBodyOverflow = null;

    /* ── Key parsing ── */

    function parseKey(keyStr) {
        if (keyStr.length === 1) {
            return { key: keyStr, modifiers: { cmd: false, shift: false, alt: false } };
        }

        var parts = keyStr.toLowerCase().split('+');
        var modifiers = { cmd: false, shift: false, alt: false };
        var key = parts.pop();

        for (var i = 0; i < parts.length; i++) {
            var mod = parts[i];
            if (mod === 'cmd' || mod === 'ctrl' || mod === 'meta') modifiers.cmd = true;
            else if (mod === 'shift') modifiers.shift = true;
            else if (mod === 'alt' || mod === 'opt') modifiers.alt = true;
        }

        var keyMap = {
            'enter': 'Enter', 'return': 'Enter',
            'escape': 'Escape', 'esc': 'Escape',
            'space': ' ',
            'up': 'ArrowUp', 'down': 'ArrowDown',
            'left': 'ArrowLeft', 'right': 'ArrowRight',
            'tab': 'Tab', 'backspace': 'Backspace', 'delete': 'Delete'
        };
        key = keyMap[key] || key;

        return { key: key, modifiers: modifiers };
    }

    /* ── Display formatting ── */

    function formatDisplay(parsed) {
        var parts = [];
        if (parsed.modifiers.cmd) parts.push('\u2318');
        if (parsed.modifiers.alt) parts.push('\u2325');
        if (parsed.modifiers.shift) parts.push('\u21e7');

        var displayMap = {
            'Enter': '\u21a9', 'Escape': 'Esc', ' ': 'Space',
            'ArrowUp': '\u2191', 'ArrowDown': '\u2193',
            'ArrowLeft': '\u2190', 'ArrowRight': '\u2192',
            'Tab': 'Tab', 'Backspace': '\u232b', 'Delete': '\u2326'
        };
        parts.push(displayMap[parsed.key] || parsed.key);
        return parts.join('');
    }

    /* ── Matching ── */

    function matches(entry, e) {
        var m = entry.modifiers;
        // cmd matches either metaKey (Mac) or ctrlKey (Win/Linux)
        if (m.cmd && !(e.metaKey || e.ctrlKey)) return false;
        if (!m.cmd && (e.metaKey || e.ctrlKey)) return false;
        // Shift only enforced when explicitly required — characters like ? and #
        // inherently involve shift, and e.key already reflects the shifted character
        if (m.shift && !e.shiftKey) return false;
        // Alt is bidirectional
        if (m.alt && !e.altKey) return false;
        if (!m.alt && e.altKey) return false;
        return e.key === entry.key;
    }

    /* ── isEditing helper ── */

    function isEditing() {
        var el = document.activeElement;
        if (!el) return false;
        var tag = el.tagName;
        if (tag === 'INPUT') return el.type !== 'checkbox';
        if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
        if (el.isContentEditable) return true;
        return false;
    }

    /* ── Registration ── */

    function register(keyStr, opts) {
        if (!opts) opts = {};
        var parsed = parseKey(keyStr);
        registry.push({
            keyStr: keyStr,
            key: parsed.key,
            modifiers: parsed.modifiers,
            display: formatDisplay(parsed),
            handler: opts.handler || null,
            label: opts.label || null,
            section: opts.section || 'Global',
            when: opts.when || null,
            allowEditing: opts.allowEditing || false,
            priority: opts.priority || 0
        });
        registry.sort(function (a, b) { return b.priority - a.priority; });
    }

    function unregister(keyStr, section) {
        var parsed = parseKey(keyStr);
        registry = registry.filter(function (entry) {
            if (entry.key !== parsed.key) return true;
            if (entry.modifiers.cmd !== parsed.modifiers.cmd) return true;
            if (entry.modifiers.shift !== parsed.modifiers.shift) return true;
            if (entry.modifiers.alt !== parsed.modifiers.alt) return true;
            if (section && entry.section !== section) return true;
            return false;
        });
    }

    /* ── Cheat Sheet ── */

    function showCheatSheet() {
        if (cheatEl) return;

        // Group shortcuts by section, preserve registration order
        var sections = {};
        var sectionOrder = [];
        for (var i = 0; i < registry.length; i++) {
            var entry = registry[i];
            if (!entry.label) continue;
            if (!sections[entry.section]) {
                sections[entry.section] = [];
                sectionOrder.push(entry.section);
            }
            sections[entry.section].push(entry);
        }

        // Build two-column layout
        var leftCol = '';
        var rightCol = '';
        var half = Math.ceil(sectionOrder.length / 2);

        for (var s = 0; s < sectionOrder.length; s++) {
            var section = sectionOrder[s];
            var html = '<h6 class="section-label">' + section + '</h6><dl>';
            var items = sections[section];
            for (var j = 0; j < items.length; j++) {
                html += '<dt><kbd>' + items[j].display + '</kbd></dt>' +
                        '<dd>' + items[j].label + '</dd>';
            }
            html += '</dl>';
            if (s < half) leftCol += html;
            else rightCol += html;
        }

        // Stacked classes (as SeeMe.overlay's stack mode): the cheat sheet can
        // open over a sheet, and the plain backdrop / modal z-index sit under
        // or tie with it, so the sheet stayed bright or covered the modal.
        cheatBackdrop = document.createElement('div');
        cheatBackdrop.className = 'backdrop backdrop--stacked show';
        cheatBackdrop.id = 'cheatsheet-backdrop';
        document.body.appendChild(cheatBackdrop);

        cheatEl = document.createElement('div');
        cheatEl.className = 'modal modal--lg modal--stacked show';
        cheatEl.id = 'cheatsheet-modal';
        cheatEl.innerHTML =
            '<div class="modal__dialog">' +
                '<div class="modal__header">' +
                    '<h3>Keyboard Shortcuts</h3>' +
                    '<button class="modal__close">' +
                        '<i class="fa-solid fa-xmark"></i>' +
                    '</button>' +
                '</div>' +
                '<div class="modal__body" style="display:grid;' +
                    'grid-template-columns:1fr 1fr;gap:0 24px;">' +
                    '<div>' + leftCol + '</div>' +
                    '<div>' + rightCol + '</div>' +
                '</div>' +
            '</div>';

        document.body.appendChild(cheatEl);
        savedBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        cheatEl.querySelector('.modal__close').addEventListener('click', closeCheatSheet);
        cheatBackdrop.addEventListener('click', closeCheatSheet);

        // Close on click outside dialog (target is the .modal wrapper itself,
        // not a child). stopPropagation prevents SeeMe.overlay's document-level
        // click handler from also firing — it watches for clicks on `.modal.show`
        // and would call overlay.close() on our cheatEl, leaving the
        // shortcuts.js state out of sync (visually closed, but cheatEl still
        // truthy → next `?` press is a no-op).
        cheatEl.addEventListener('click', function (e) {
            if (e.target === cheatEl) {
                closeCheatSheet();
                e.stopPropagation();
            }
        });
    }

    function closeCheatSheet() {
        if (cheatEl) { cheatEl.remove(); cheatEl = null; }
        if (cheatBackdrop) { cheatBackdrop.remove(); cheatBackdrop = null; }
        document.body.style.overflow = savedBodyOverflow || '';
        savedBodyOverflow = null;
    }

    function isCheatSheetOpen() {
        return !!cheatEl;
    }

    /* ── Main Keydown Handler ── */

    document.addEventListener('keydown', function (e) {

        // Escape closes cheat sheet (always, regardless of editing state)
        if (e.key === 'Escape' && cheatEl) {
            closeCheatSheet();
            e.preventDefault();
            return;
        }

        // Don't dispatch shortcuts while spotlight is open —
        // spotlight has its own capture-phase listener for Cmd+K and Escape
        if (SeeMe.spotlight && SeeMe.spotlight.isOpen()) return;

        for (var i = 0; i < registry.length; i++) {
            var entry = registry[i];
            if (!matches(entry, e)) continue;
            if (!entry.handler) continue;
            if (!entry.allowEditing && isEditing()) continue;
            if (cheatEl) continue;
            if (entry.when && !entry.when()) continue;

            e.preventDefault();
            entry.handler(e);
            return;
        }
    });

    // Built-in: ? shows cheat sheet
    register('?', {
        handler: function () { showCheatSheet(); },
        label: 'Keyboard shortcuts',
        section: 'Global',
        priority: -100
    });

    return {
        register: register,
        unregister: unregister,
        showCheatSheet: showCheatSheet,
        closeCheatSheet: closeCheatSheet,
        isCheatSheetOpen: isCheatSheetOpen,
        isEditing: isEditing
    };

})();

window.SeeMe = SeeMe;
